import React from "react";
import { Button } from "@/components/ui/button";
import { ChargeType, PricingModel, ServiceItem } from "../types";
import { SECTIONS } from "../consts";
import { useServiceItemTypes, usePricingModelOptions } from "../hooks";
import { useServiceItemForm } from "../hooks/useServiceItemForm";
import { useCurrencySymbol } from "../hooks/useCurrencySymbol";
import Text from "@/components/ui/text";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FieldType } from "@/components/controls/Form/types";
import { useTranslation } from "react-i18next";
import { getChargeTypeFromNumber } from "../utils";
import PricingTable from "./PricingTable";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import Banner from "@/components/ui/banner";
import toast from "react-hot-toast";

const TRANSLATION_KEY = "settings.product";

interface EditServiceItemDrawerProps {
  isOpen: boolean;
  productId: string;
  serviceItem: ServiceItem;
  // Shows the bundle copy in the update confirmation.
  isInBundles?: boolean;
  onClose: () => void;
  onSave?: () => void;
  onRefetchProducts?: () => void;
}

const EditServiceItemDrawer: React.FC<EditServiceItemDrawerProps> = ({
  isOpen,
  productId,
  serviceItem,
  isInBundles = false,
  onClose,
  onSave,
  onRefetchProducts
}) => {
  const { t } = useTranslation();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    React.useState(false);

  const {
    formState,
    isFormValid,
    formRef,
    setFormSetValue,
    handleFormValid,
    handleFormSubmit,
    isSubmitting,
    setPricing,
    isUpdateOnly
  } = useServiceItemForm({
    serviceItem,
    productId,
    isEditMode: true
  });

  const currencySymbol = useCurrencySymbol();
  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  const { data: pricingModelOptions = [] } = usePricingModelOptions();

  const typeOptions = React.useMemo(() => {
    const options: any[] = [];

    SECTIONS.forEach((section) => {
      const sectionTypes = serviceItemTypes.filter(
        (type) => type.sectionId === section.id
      );

      sectionTypes.forEach((type) => {
        options.push({
          id: type.id,
          displayName: type.name,
          section: section.id.toString()
        });
      });
    });

    return options;
  }, [serviceItemTypes]);

  const memoizedPricingModelOptions = React.useMemo(
    () =>
      pricingModelOptions.map((option) => ({
        id: option.id,
        displayName: option.name,
        subtitle: <Text variant="smBodySecondary">{option.description}</Text>,
        isHidden: false,
        disabled: !option.isEnabled,
        comingSoon: !option.isEnabled
      })),
    [pricingModelOptions]
  );

  // Get initial charge type from serviceItem (only used for defaultValue)
  const initialChargeType = React.useMemo(
    () => getChargeTypeFromNumber(serviceItem.chargeType as unknown as number),
    [serviceItem.chargeType]
  );

  const sections: FormSectionProps[] = React.useMemo(
    () => [
      {
        id: "serviceItemInfo",
        divider: false,
        fields: [
          {
            name: "typeId",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.type`),
            type: "singleSelect",
            required: true,
            defaultValue: serviceItem.typeId,
            fieldActionProps: {
              placeholder: t(
                `${TRANSLATION_KEY}.serviceItemDrawer.typePlaceholder`
              ),
              options: typeOptions,
              showSelectedItemSubtitle: false,
              enableSections: true,
              sections: SECTIONS.map((section) => ({
                id: section.id.toString(),
                title: t(section.name)
              }))
            }
          },
          {
            name: "name",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.nameSku`),
            type: "text",
            required: true,
            defaultValue: serviceItem.name,
            fieldActionProps: {
              placeholder: t(
                `${TRANSLATION_KEY}.serviceItemDrawer.namePlaceholder`
              )
            },
            subLabel: t(`${TRANSLATION_KEY}.serviceItemDrawer.nameSubLabel`)
          },
          {
            name: "description",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.description`),
            type: "textarea",
            required: false,
            defaultValue: serviceItem.description || "",
            fieldActionProps: {
              placeholder: t(
                `${TRANSLATION_KEY}.serviceItemDrawer.descriptionPlaceholder`
              ),
              minRows: 1
            },
            fieldClassName: "border-b-[0.5px] border-border-border1 pb-4"
          },
          {
            name: "chargeType",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.chargeType`),
            type: "tabs",
            required: true,
            defaultValue: initialChargeType,
            fieldActionProps: {
              options: {
                [ChargeType.RECURRING]: t(
                  `${TRANSLATION_KEY}.chargeType.recurring`
                ),
                [ChargeType.ONE_TIME]: t(
                  `${TRANSLATION_KEY}.chargeType.oneTime`
                )
              },
              fullWidth: true
            }
          },
          {
            name: "pricingModel",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.pricingModel`),
            type: "singleSelect",
            required: true,
            defaultValue: serviceItem.pricingModel,
            fieldActionProps: {
              placeholder: t(`${TRANSLATION_KEY}.serviceItemDrawer.select`),
              options: memoizedPricingModelOptions,
              showSelectedItemSubtitle: false
            },
            subLabel: t(
              `${TRANSLATION_KEY}.serviceItemDrawer.pricingModelSubLabel`
            )
          }
        ]
      }
    ],
    [
      serviceItem.id,
      serviceItem.typeId,
      serviceItem.name,
      serviceItem.description,
      serviceItem.pricingModel,
      initialChargeType,
      currencySymbol,
      typeOptions,
      memoizedPricingModelOptions,
      t
    ]
  );

  // Separate the oneTimePrice field to avoid infinite loops when chargeType changes
  const oneTimePriceField = React.useMemo(
    () => ({
      name: "oneTimePrice",
      label: t(`${TRANSLATION_KEY}.serviceItemDrawer.price`),
      type: "number" as FieldType,
      required: true,
      defaultValue:
        serviceItem.pricing.length > 0 ? serviceItem.pricing[0].price : 0,
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.serviceItemDrawer.pricePlaceholder`),
        trailingIcon: (
          <Text variant="body" className="text-gray-500">
            {currencySymbol}
          </Text>
        )
      }
    }),
    [serviceItem.pricing, currencySymbol, t]
  );

  // Combine sections with oneTimePrice field based on current chargeType
  const finalSections = React.useMemo(() => {
    if (
      formState.chargeType === ChargeType.ONE_TIME &&
      formState.formData.pricingModel !== PricingModel.FLEXIBLE
    ) {
      const baseFields = sections[0].fields || [];
      return [
        {
          ...sections[0],
          fields: [...baseFields, oneTimePriceField]
        }
      ];
    }
    return sections;
  }, [
    sections,
    oneTimePriceField,
    formState.chargeType,
    formState.formData.pricingModel
  ]);

  const handleSave = async () => {
    try {
      await handleFormSubmit();
    } catch {
      toast.error(t(`${TRANSLATION_KEY}.serviceItemDrawer.saveError`));
      return;
    }
    onRefetchProducts?.();
    onSave?.();
    onClose();
  };

  const handleSaveButtonClick = async () => {
    // Asks when type/chargeType/pricingModel changed; a service in bundles always asks, as any change reaches them.
    if (isUpdateOnly() && !isInBundles) {
      await handleSave();
    } else {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmUpdate = async () => {
    setIsConfirmationModalOpen(false);
    await handleSave();
  };

  return (
    <Drawer
      open={isOpen}
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={false}
      noBodyStyles={true}
      dismissible={false}
    >
      <AppDrawerContent
        className="p-0 overflow-x-hidden w-full"
        data-test="products-edit-service-drawer"
        isSettingsDrawer
      >
        <DrawerHeader>
          <DrawerTitle>
            <Text
              variant="lgSubtitle"
              data-test="products-edit-service-drawer-title"
            >
              {t(`${TRANSLATION_KEY}.serviceItemDrawer.editService`)}
            </Text>
          </DrawerTitle>
          <IconButton
            onClick={onClose}
            variant="iconNoBg"
            size="sm"
            data-test="products-edit-service-drawer-close-button"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="px-5 py-5 w-full overflow-y-scroll">
          <FormNew
            sections={finalSections}
            isFormValid={handleFormValid}
            onSubmit={handleSaveButtonClick}
            formRef={formRef}
            onSetValue={setFormSetValue}
            shouldNotResetOnSubmit={true}
            key={`edit-service-item-form-${serviceItem.id}`}
          />

          {formState.formData.pricingModel === PricingModel.FLEXIBLE ? (
            <Banner
              variant="info"
              title={t(
                `${TRANSLATION_KEY}.serviceItemDrawer.flexiblePricingInfo`
              )}
              showCloseButton={false}
              titleVariant="body"
            />
          ) : (
            formState.chargeType === ChargeType.RECURRING && (
              <div className="mt-2 pt-6">
                <PricingTable
                  pricing={formState.pricing}
                  onPricingChange={setPricing}
                  chargeType={formState.chargeType}
                />
              </div>
            )
          )}
        </div>

        <DrawerFooter>
          <div className="flex justify-end gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
            <Button
              variant="secondaryNoBg"
              onClick={onClose}
              data-test="products-edit-service-drawer-cancel-button"
            >
              {t(`${TRANSLATION_KEY}.serviceItemDrawer.cancel`)}
            </Button>
            <Button
              disabled={!isFormValid || isSubmitting}
              onClick={handleSaveButtonClick}
              data-test="products-edit-service-drawer-save-button"
            >
              {t(`${TRANSLATION_KEY}.serviceItemDrawer.save`)}
            </Button>
          </div>
        </DrawerFooter>
      </AppDrawerContent>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        title={t(
          `${TRANSLATION_KEY}.serviceItemDrawer.updateConfirmation.title`
        )}
        confirmationText={t(
          `${TRANSLATION_KEY}.serviceItemDrawer.updateConfirmation.${
            isInBundles ? "messageInBundles" : "message"
          }`
        )}
        handleSubmit={handleConfirmUpdate}
        handleCancel={() => setIsConfirmationModalOpen(false)}
        isPrimaryButton={true}
        submitButtonText={t(
          `${TRANSLATION_KEY}.serviceItemDrawer.updateConfirmation.confirm`
        )}
        cancelButtonText={t(
          `${TRANSLATION_KEY}.serviceItemDrawer.updateConfirmation.cancel`
        )}
        hideDivider
        headerClassname="border-none"
        data-test="products-edit-service-drawer-confirm-modal"
      />
    </Drawer>
  );
};

export default EditServiceItemDrawer;
